import { RunStatus } from '../../../../backend/src/types/types';

export type ProcessingStatus = Extract<
  RunStatus,
  | RunStatus.AwaitingApproval
  | RunStatus.InQueue
  | RunStatus.Parsing
  | RunStatus.Extracting
  | RunStatus.Clustering
  | RunStatus.IdentifyingRelevance
  | RunStatus.IdentifyingInconsistencies
  | RunStatus.Summarising
>;

export const PipelineProcessingStageDescriptions: Record<
  ProcessingStatus,
  { title: string; subTitle: string; description: string; weight: number }
> = {
  [RunStatus.AwaitingApproval]: {
    title: 'Approval',
    subTitle: '',
    description: 'Approve or cancel this run before processing continues',
    weight: 0,
  },
  [RunStatus.InQueue]: {
    title: 'Queuing Documents',
    subTitle: 'In Queue...',
    description: 'Documents are added to the queue for processing.',
    weight: 0,
  },
  [RunStatus.Parsing]: {
    title: 'Estimating Processing Time',
    subTitle: 'Estimating completion time...',
    description: 'Pages are counted during upload, so an estimated completion time is calculated straight away.',
    weight: 1,
  },
  [RunStatus.Extracting]: {
    title: 'Extracting Facts',
    subTitle: 'Extracting facts...',
    description: 'OCR is performed on documents before facts are identified and extracted.',
    weight: 6,
  },
  [RunStatus.Clustering]: {
    title: 'Clustering Facts',
    subTitle: 'Clustering Facts...',
    description: 'Facts expressing the same event are merged together to avoid duplicate facts.',
    weight: 2,
  },
  [RunStatus.IdentifyingRelevance]: {
    title: 'Classifying Relevance',
    subTitle: 'Identifying Relevance...',
    description:
      'Each fact is evaluated against the Matter context to assess its individual relevance and significance.',
    weight: 4,
  },
  [RunStatus.IdentifyingInconsistencies]: {
    title: 'Identifying Inconsistencies',
    subTitle: 'Identifying Inconsistencies...',
    description:
      'Semantically similar facts are compared to find inconsistencies, and evaluated against the Matter issues to determine severity.',
    weight: 2,
  },
  [RunStatus.Summarising]: {
    title: 'Finalising Generation',
    subTitle: 'Generating Summary...',
    description:
      'Key entities are identified and mapped to facts. Summaries are generated for both the Matter and individual issues.',
    weight: 1,
  },
};

export const PipelineProcessingStages = Object.keys(PipelineProcessingStageDescriptions) as ProcessingStatus[];

export const isProcessingStatus = (status: RunStatus): status is ProcessingStatus => {
  return PipelineProcessingStages.includes(status as ProcessingStatus);
};

/** Human-readable label for every run status, including the terminal ones. */
export const RunStatusLabels: Record<RunStatus, string> = {
  [RunStatus.InQueue]: 'In queue',
  [RunStatus.Parsing]: 'Parsing',
  [RunStatus.AwaitingApproval]: 'Awaiting approval',
  [RunStatus.Extracting]: 'Extracting',
  [RunStatus.Clustering]: 'Clustering',
  [RunStatus.IdentifyingRelevance]: 'Identifying relevance',
  [RunStatus.IdentifyingInconsistencies]: 'Identifying inconsistencies',
  [RunStatus.Summarising]: 'Summarising',
  [RunStatus.Complete]: 'Complete',
  [RunStatus.Failed]: 'Failed',
  [RunStatus.Cancelled]: 'Cancelled',
  [RunStatus.ApprovalTimeout]: 'Approval timeout',
};

export const AllRunStatuses = Object.keys(RunStatusLabels) as RunStatus[];

/**
 * Badge colours for run statuses: green for success, red for failure, grey for the
 * other terminal states, amber while a run needs a human, blue while it's working.
 */
export const RunStatusBadgeClasses: Record<RunStatus, string> = {
  [RunStatus.Complete]: 'border-green-200 bg-green-50 text-green-600',
  [RunStatus.Failed]: 'border-red-200 bg-red-50 text-red-600',
  [RunStatus.Cancelled]: 'border-gray-200 bg-gray-50 text-gray-600',
  [RunStatus.ApprovalTimeout]: 'border-gray-200 bg-gray-50 text-gray-600',
  [RunStatus.AwaitingApproval]: 'border-amber-200 bg-amber-50 text-amber-700',
  [RunStatus.InQueue]: 'border-sky-200 bg-sky-50 text-sky-700',
  [RunStatus.Parsing]: 'border-sky-200 bg-sky-50 text-sky-700',
  [RunStatus.Extracting]: 'border-sky-200 bg-sky-50 text-sky-700',
  [RunStatus.Clustering]: 'border-sky-200 bg-sky-50 text-sky-700',
  [RunStatus.IdentifyingRelevance]: 'border-sky-200 bg-sky-50 text-sky-700',
  [RunStatus.IdentifyingInconsistencies]: 'border-sky-200 bg-sky-50 text-sky-700',
  [RunStatus.Summarising]: 'border-sky-200 bg-sky-50 text-sky-700',
};
